<template>
  <form
    id="search-form"
    @submit.prevent="searchCalls">
    <v-container class="px-5">
      <!-- Customer -->
      <v-row
        v-if="isPermittedAll([
          'CustomersGetAll',
        ])">
        <v-col class="pt-0">
          <h3 class="mb-3">Filter</h3>
          <label for="search-customer">Customer:</label>
          <Autocomplete
            id="search-customer"
            :items="$store.state.allCustomers"
            v-model="customerSearchParameter"
            item-value="CustomerID"
            item-text="CustomerName"
            outlined
            filled
            include-any>
            <template v-slot:item="{ item }">
              <div class="w-100 d-flex align-center">
                <div class="mr-auto">{{item.CustomerName}}</div>
                <span
                  v-if="item.CustomerStatusColor"
                  class="customer-status-dot"
                  :class="item.CustomerStatusColor.CustomerStatusColorName">
                </span>
              </div>
            </template>
            <template v-slot:selection="{ item }">
              <div class="w-100 d-flex align-center">
                <div class="text-truncate mr-auto">{{item.CustomerName}}</div>
                <span
                  v-if="item.CustomerStatusColor"
                  class="customer-status-dot"
                  :class="item.CustomerStatusColor.CustomerStatusColorName">
                </span>
              </div>
            </template>
          </Autocomplete>
        </v-col>
      </v-row>
      <!-- / Customer -->

      <!-- Customer Details -->
      <CustomerDetails
        v-if="selectedCustomer
          && isPermittedAll([
            'CustomersGetByID',
          ])"
        :customer="selectedCustomer"
        :dense="true"
        :hide-fax="true"
      />
      <!-- / Customer Details -->

      <!-- Product Key -->
      <v-row
        v-if="isPermittedAll([
          'ComputersGetAll',
        ])">
        <v-col>
          <label for="search-product-key">Product Key:</label>
          <Autocomplete
            id="search-product-key"
            :items="$store.getters.productKeys"
            v-model="productKeySearchParameter"
            item-text="ProductKeyName"
            item-value="ProductKey"
            outlined
            filled
            include-any>
          </Autocomplete>
        </v-col>
      </v-row>
      <!-- / Product Key -->

      <!-- Serial Number -->
      <v-row
        v-if="isPermittedAll([
          'ComputersGetAll',
        ])">
        <v-col>
          <label for="search-serial-number">Serial Number:</label>
          <Autocomplete
            id="search-serial-number"
            :items="$store.getters.serialNumbers"
            v-model="serialNumberSearchParameter"
            item-text="SerialNumberName"
            item-value="SerialNumber"
            outlined
            filled
            include-any>
          </Autocomplete>
        </v-col>
      </v-row>
      <!-- / Serial Number -->

      <!-- Phone -->
      <v-row
        v-if="isPermittedAll([
          'CustomersGetAll',
        ])">
        <v-col>
          <label for="search-phone">Employee Phone:</label>
          <Autocomplete
            id="search-phone"
            :items="$store.getters.employeePhoneNumbers"
            v-model="phoneNumberSearchParameter"
            item-text="PhoneNumberName"
            item-value="PhoneNumber"
            outlined
            filled
            include-any>
          </Autocomplete>
        </v-col>
      </v-row>
      <!-- / Phone -->

      <!-- Employee Name -->
      <v-row
        v-if="isPermittedAll([
          'EmployeesGetAll',
        ])">
        <v-col>
          <label for="search-employee-name">Employee Name:</label>
          <Autocomplete
            id="search-employee-name"
            :items="$store.state.employees"
            v-model="employeeSearchParameter"
            item-value="EmployeeID"
            item-text="EmployeeName"
            outlined
            filled
            include-any>
          </Autocomplete>
        </v-col>
      </v-row>
      <!-- / Employee Name -->

      <!-- Start-->
      <v-row>
        <v-col>
          <label for="calls-search-start">Calls Started Between:</label>
          <DatePicker
            input-id="calls-search-start"
            :line-bottom="false"
            v-model="callLogSearchParameters.StartDateUtc"
            @input="$store.commit(
              'setSearchParameters',
              { params: {
                StartDateUtc: callLogSearchParameters.StartDateUtc,
              }, paramsKey: 'callLogSearchParameters' },
            )"
          />
        </v-col>
      </v-row>
      <!-- / Start -->

      <!-- End-->
      <v-row>
        <v-col>
          <DatePicker
            input-id="calls-search-end"
            :line-bottom="false"
            v-model="callLogSearchParameters.EndDateUtc"
            @input="$store.commit(
              'setSearchParameters',
              { params: {
                EndDateUtc: callLogSearchParameters.EndDateUtc,
              }, paramsKey: 'callLogSearchParameters' },
            )"
          />
        </v-col>
      </v-row>
      <!-- / End -->

      <!-- Reset Date Range -->
      <v-row>
        <v-col>
          <v-btn
            type="button"
            color="primary"
            :disabled="$store.getters.isLoadingActive"
            @click="resetDateRangeFilter"
            outlined
            block
          >
            Reset Date Range
          </v-btn>
        </v-col>
      </v-row>
      <!-- / Reset Date Range -->

      <!-- Hidden -->
      <v-row
        v-if="isPermittedAll([
          'CallsViewDeleted'
        ])">
        <v-col>
          <label for="search-include-hidden">Hidden:</label>
          <Autocomplete
            id="search-include-hidden"
            :items="$store.state.hiddenStatuses"
            v-model="hiddenStatusSearchParameter"
            outlined
            filled>
          </Autocomplete>
        </v-col>
      </v-row>
      <!-- / Hidden -->

      <!-- Search -->
      <v-row>
        <v-col>
          <v-btn
            type="submit"
            color="success"
            outlined
            block
            :disabled="$store.getters.isLoadingActive">
            Search Calls
          </v-btn>
        </v-col>
      </v-row>
      <!-- / Search -->
    </v-container>
  </form>
</template>

<script>
import _ from 'lodash';
import CustomerDetails from './CustomerDetails.vue';
import DatePicker from './DatePicker.vue';

export default {
  name: 'CallLogSearchForm',
  components: {
    CustomerDetails,
    DatePicker,
  },
  computed: {
    /* Parameters */
    customerSearchParameter: {
      get() {
        let customer = _.find(
          this.$store.state.allCustomers,
          { CustomerID: this.callLogSearchParameters.CustomerID },
        );

        if (!customer) {
          customer = {
            CustomerID: undefined,
            CustomerName: this.anyOption,
            CustomerStatusColor: {},
          };
        }

        return _.pick(customer, ['CustomerID', 'CustomerName', 'CustomerStatusColor']);
      },
      set(customerSearchParameter) {
        const params = {
          CustomerID: customerSearchParameter
            && customerSearchParameter.CustomerID
            ? customerSearchParameter.CustomerID
            : undefined,
        };

        this.$store.commit(
          'setSearchParameters',
          { paramsKey: 'callLogSearchParameters', params },
        );
      },
    },
    productKeySearchParameter: {
      get() {
        let productKey = _.find(
          this.$store.getters.productKeys,
          { ProductKey: this.callLogSearchParameters.ProductKey },
        );

        if (!productKey) {
          productKey = {
            ProductKey: undefined,
            ProductKeyName: this.anyOption,
          };
        }

        return _.pick(productKey, ['ProductKey', 'ProductKeyName']);
      },
      set(productKeySearchParameter) {
        const params = {
          ProductKey: productKeySearchParameter.ProductKey || undefined,
        };

        this.$store.commit(
          'setSearchParameters',
          { paramsKey: 'callLogSearchParameters', params },
        );
      },
    },
    serialNumberSearchParameter: {
      get() {
        let serialNumber = _.find(
          this.$store.getters.serialNumbers,
          { SerialNumber: this.callLogSearchParameters.SerialNumber },
        );

        if (!serialNumber) {
          serialNumber = {
            SerialNumber: undefined,
            SerialNumberName: this.anyOption,
          };
        }

        return _.pick(serialNumber, ['SerialNumber', 'SerialNumberName']);
      },
      set(serialNumberSearchParameter) {
        const params = {
          SerialNumber: serialNumberSearchParameter.SerialNumber || undefined,
        };

        this.$store.commit(
          'setSearchParameters',
          { paramsKey: 'callLogSearchParameters', params },
        );
      },
    },
    phoneNumberSearchParameter: {
      get() {
        let phoneNumber = _.find(
          this.$store.getters.employeePhoneNumbers,
          { PhoneNumber: this.callLogSearchParameters.PhoneNumber },
        );

        if (!phoneNumber) {
          phoneNumber = {
            PhoneNumber: undefined,
            PhoneNumberName: this.anyOption,
          };
        }

        return _.pick(phoneNumber, ['PhoneNumber', 'PhoneNumberName']);
      },
      set(phoneNumberSearchParameter) {
        const params = {
          PhoneNumber: phoneNumberSearchParameter.PhoneNumber || undefined,
        };

        this.$store.commit(
          'setSearchParameters',
          { paramsKey: 'callLogSearchParameters', params },
        );
      },
    },
    employeeSearchParameter: {
      get() {
        let employee = _.find(
          this.$store.state.employees,
          { EmployeeID: this.callLogSearchParameters.EmployeeID,
            EmployeeName: this.callLogSearchParameters.EmployeeName },
        );

        if (!employee) {
          employee = {
            EmployeeID: undefined,
            EmployeeName: this.anyOption,
          };
        }

        return _.pick(employee, ['EmployeeID', 'EmployeeName']);
      },
      set(employeeSearchParameter) {
        const params = { EmployeeID: undefined, EmployeeName: undefined };

        if (employeeSearchParameter.EmployeeID) {
          params.EmployeeName = employeeSearchParameter.EmployeeName;
          params.EmployeeID = employeeSearchParameter.EmployeeID;
        }

        this.$store.commit(
          'setSearchParameters',
          { paramsKey: 'callLogSearchParameters', params },
        );
      },
    },
    hiddenStatusSearchParameter: {
      get() {
        let hiddenStatus = _.find(
          this.$store.state.hiddenStatuses,
          { value: this.callLogSearchParameters.HiddenStatus },
        );

        if (!hiddenStatus) {
          hiddenStatus = {
            value: undefined,
            text: this.anyOption,
          };
        }

        return hiddenStatus;
      },
      set(hiddenStatusSearchParameter) {
        const params = {
          HiddenStatus: hiddenStatusSearchParameter.value || undefined,
        };

        this.$store.commit(
          'setSearchParameters',
          { paramsKey: 'callLogSearchParameters', params },
        );
      },
    },
    /* / Parameters */

    selectedCustomer() {
      return _.find(
        this.$store.state.allCustomers,
        { CustomerID: this.customerSearchParameter.CustomerID },
      );
    },

    callLogSearchParameters() {
      return this.$store.state.callLogSearchParameters;
    },
  },
  methods: {
    searchCalls() {
      this.$store.dispatch('getCalls');
      this.$store.dispatch('getOpenCalls');
    },
    resetDateRangeFilter() {
      this.$store.dispatch('resetDateRangeFilter');
      this.searchCalls();
    },
  },
  created() {
    this.$store.dispatch('getAllCustomers');
    this.$store.dispatch('getEmployees');
    this.$store.dispatch('getComputers');
  },
};
</script>

<style lang="scss"></style>
